import React, { useState } from "react";
import { createPortal } from "react-dom";
import { motion } from "framer-motion";
import { X, Loader2, Wand2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import api from "@/lib/api";

/**
 * Lightweight inline modal for "+ Add New Program" inside the client form.
 * Saves immediately to the trainer's program library, then calls `onCreated`
 * with the new program doc so the parent can auto-select it.
 */
export default function ProgramQuickCreateModal({ onCancel, onCreated, defaultName = "" }) {
  const [form, setForm] = useState({
    name: defaultName,
    description: "",
    defaultPrice: "",
    defaultSessionCount: "",
  });
  const [submitting, setSubmitting] = useState(false);

  async function handleSubmit(e) {
    e.preventDefault();
    if (!form.name.trim()) {
      toast.error("Program name is required");
      return;
    }
    setSubmitting(true);
    try {
      const body = {
        name: form.name.trim(),
        description: form.description.trim(),
        defaultPrice: form.defaultPrice ? Number(form.defaultPrice) : 0,
        defaultSessionCount: form.defaultSessionCount ? parseInt(form.defaultSessionCount, 10) : 0,
      };
      const res = await api.post("/training-programs", body);
      toast.success(`Created "${res.data.name}"`);
      if (typeof onCreated === "function") onCreated(res.data);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Could not create program");
    } finally {
      setSubmitting(false);
    }
  }

  return createPortal(
    <div
      data-testid="program-quick-create-modal"
      className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 p-4"
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.96 }}
        animate={{ opacity: 1, scale: 1 }}
        className="w-full max-w-md rounded-3xl bg-white p-6 shadow-xl"
      >
        <div className="mb-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Wand2 size={20} />
            <h2 className="text-xl font-bold">Add Training Program</h2>
          </div>
          <Button
            type="button"
            variant="outline"
            onClick={onCancel}
            className="rounded-2xl"
            data-testid="program-quick-cancel-btn"
          >
            <X size={16} />
          </Button>
        </div>
        <form onSubmit={handleSubmit} className="grid gap-3">
          <label className="space-y-1 text-sm font-medium">
            Program name
            <input
              data-testid="program-quick-name"
              required
              autoFocus
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="e.g. Adolescent Manners"
              className="w-full rounded-xl border p-2 font-normal"
            />
          </label>
          <div className="grid grid-cols-2 gap-3">
            <label className="space-y-1 text-sm font-medium">
              Default price
              <input
                data-testid="program-quick-price"
                type="number"
                min="0"
                step="0.01"
                value={form.defaultPrice}
                onChange={(e) => setForm({ ...form, defaultPrice: e.target.value })}
                placeholder="500"
                className="w-full rounded-xl border p-2 font-normal"
              />
            </label>
            <label className="space-y-1 text-sm font-medium">
              Sessions
              <input
                data-testid="program-quick-sessions"
                type="number"
                min="0"
                step="1"
                value={form.defaultSessionCount}
                onChange={(e) => setForm({ ...form, defaultSessionCount: e.target.value })}
                placeholder="6"
                className="w-full rounded-xl border p-2 font-normal"
              />
            </label>
          </div>
          <label className="space-y-1 text-sm font-medium">
            Description (optional)
            <textarea
              data-testid="program-quick-description"
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              rows={2}
              placeholder="What's included, who it's for, etc."
              className="w-full rounded-xl border p-2 font-normal"
            />
          </label>
          <div className="mt-2 flex gap-2">
            <Button
              data-testid="program-quick-save-btn"
              type="submit"
              disabled={submitting}
              className="rounded-2xl"
            >
              {submitting ? (
                <><Loader2 size={14} className="mr-1.5 animate-spin" />Saving…</>
              ) : (
                "Save & select"
              )}
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={onCancel}
              className="rounded-2xl"
            >
              Cancel
            </Button>
          </div>
          <p className="text-xs text-slate-500">
            Saves to your program library — you can edit or archive it later in <strong>Programs</strong>.
          </p>
        </form>
      </motion.div>
    </div>,
    document.body
  );
}
